import CheckIcon from '@mui/icons-material/Check';
import SearchIcon from '@mui/icons-material/Search';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Stack,
    Typography,
    Tooltip,
} from '@mui/material';
import BaseTextField from 'common/components/base-text-field';
import FormProvider from 'common/components/form-provider';
import React, {
    useEffect,
    useRef,
    useState,
    useCallback,
    useMemo,
} from 'react';
import { useForm } from 'react-hook-form';
import QuestionNewEditForm from './components/question-new-edit-form';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { common } from '@mui/material/colors';
import { colors } from 'common/constants';
import { createStyleSheet, handleError } from 'utils/helpers';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { ReactComponent as AddQuestionIcon } from 'assets/images/icons/question-design/plus-question.svg';
import { IQuestionNewEditForm } from 'types/campaign-design';
import { ReactComponent as EditCampaign } from 'assets/images/icons/question-design/pencil-question.svg';
import { ReactComponent as SaveQuestionIcon } from 'assets/images/icons/question-design/save-question.svg';
import ModalUplooadFileQuestion from './components/upload-file-question/modal-upload-file-question';
import {
    createCampaignQuestion,
    listCampaignQuestions,
    updateCampaignQuestion,
    swapCampaignQuestion,
    deleteCampaignQuestion,
    markCampaignQuestionAsRequired,
    duplicateCampaignQuestion,
} from 'services/campaign.service';
import { useNavigate, useParams } from 'react-router-dom';
import { listCampaignQuestionsFormatter } from 'utils/campaign_helpers';
import ModalConfirmSwapQuestion from './components/swap-position/modal-confirm-swap-question';
import { useAppDispatch, useAppSelector } from 'common/hook';
import { RootState } from 'plugins/store';
import {
    setSelectedQuestionIndex,
    setCampaignQuestions,
} from 'slices/campaignsSlice';
import { toast } from 'react-toastify';
import { CampaignContainer } from '../campaign-overview/campaign-container';
import CampaignForm from '../campaign/components/campaign-form';
import CampaignStatus from '../campaign/components/campaign-status';
import { setIsLoading } from 'slices/loadingSlice';
import NumbersIcon from '@mui/icons-material/Numbers';
import QuotaForm from './components/quota/quota-form';

const DEFAULT_FIRST_QUESTION = {
    id: '',
    question: '',
    questionType: 'textbox',
    data: null,
    required: false,
    questionNumber: 1,
};

const DEFAULT_FIRST_QUESTION_PUBLIC_WITH_CODE = {
    id: '',
    question: '',
    questionType: 'single_choice',
    data: {
        answers: [
            {
                answer: 'Tùy chọn 1',
                id: null,
                image: null,
                type: '1',
            },
        ],
        numberOfRows: 1,
    },
    required: false,
    questionNumber: 1,
};

const DEFAULT_QUESTION_CONTENT = 'Nhập nội dung câu hỏi?';

const CampaignDesign: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const selectedIndex = useAppSelector(
        (state: RootState) => state.campaignState.selectedQuestionIndex
    );
    const [openUploadFileExcel, setOpenUploadFileExcel] = React.useState(false);
    const questions = useAppSelector(
        (state: RootState) => state.campaignState.campaignQuestions
    );
    const currentCampaignId = Number(useParams().id);
    const [isModalOpenDrag, setIsModalOpenDrag] = useState(false);
    const [dragSource, setDragSource] = useState<IQuestionNewEditForm | null>(
        null
    );
    const currentCampaign = useAppSelector(
        (state) => state.campaignState.campaign
    );
    const [openModalCampaign, setOpenModalCampaign] =
        React.useState<boolean>(false);
    const [dragTarget, setDragTarget] = useState<IQuestionNewEditForm | null>(
        null
    );
    const isCampaignPublicWithCode = useMemo(() => {
        return currentCampaign && currentCampaign?.kind === 'public_with_code';
    }, [currentCampaign?.kind]);

    useEffect(() => {
        // select and scroll element into view base on id='question-selectedIndex'
        const element = document.getElementById(`question-${selectedIndex}`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [selectedIndex]);

    const getCampaignQuestions = useCallback(async () => {
        if (!currentCampaign) {
            return;
        }
        dispatch(setIsLoading(true));
        try {
            const listQuestionReponse =
                await listCampaignQuestions(currentCampaignId);
            if (listQuestionReponse.success) {
                const formattedQuestions =
                    listQuestionReponse.data.questions.map(
                        (
                            item: any // eslint-disable-line @typescript-eslint/no-explicit-any
                        ) => listCampaignQuestionsFormatter(item)
                    );

                if (formattedQuestions.length > 0) {
                    dispatch(setCampaignQuestions(formattedQuestions));
                    dispatch(setSelectedQuestionIndex(null));
                    setTimeout(() => {
                        dispatch(setIsLoading(false));
                    }, 100);
                } else {
                    const isPublicWithCode =
                        currentCampaign?.kind === 'public_with_code';

                    if (isPublicWithCode) {
                        dispatch(
                            setCampaignQuestions([
                                DEFAULT_FIRST_QUESTION_PUBLIC_WITH_CODE,
                            ])
                        );
                    } else {
                        dispatch(
                            setCampaignQuestions([DEFAULT_FIRST_QUESTION])
                        );
                    }

                    dispatch(setSelectedQuestionIndex(0));
                    dispatch(setIsLoading(false));
                }
            }
        } catch (error) {
            dispatch(setIsLoading(false));
            handleError(error);
        }
    }, [currentCampaignId, dispatch, currentCampaign]);

    useEffect(() => {
        if (currentCampaign) {
            dispatch(setCampaignQuestions([]));
            getCampaignQuestions();
        }
    }, [currentCampaign, currentCampaignId]);
    const methods = useForm({
        defaultValues: {
            question: '',
        },
    });

    const { handleSubmit } = methods;

    const onSearch = handleSubmit((data) => {
        console.log(data);
    });

    const handleItemSelect = async (index: number) => {
        if (selectedIndex === index) {
            return;
        } else if (selectedIndex === null || selectedIndex === undefined) {
            dispatch(setSelectedQuestionIndex(index));
            return;
        } else {
            dispatch(setIsLoading(true));
            const callback = (questionsList: IQuestionNewEditForm[]) => {
                dispatch(setSelectedQuestionIndex(index));
                dispatch(setCampaignQuestions(questionsList));
                dispatch(setIsLoading(false));
            };
            checkCurrentSelectQuestionAndSubmit(callback);
        }
    };

    const addQuestionToList = async (
        questionList: IQuestionNewEditForm[],
        newQuestion: IQuestionNewEditForm
    ) => {
        let newQuestions;
        if (
            selectedIndex !== null &&
            selectedIndex !== undefined &&
            selectedIndex < questionList.length
        ) {
            newQuestions = [
                ...questionList.slice(0, selectedIndex + 1),
                newQuestion,
                ...questionList.slice(selectedIndex + 1),
            ];
        } else {
            newQuestions = [...questionList, newQuestion];
        }
        dispatch(setCampaignQuestions(newQuestions));
        if (selectedIndex !== null && selectedIndex !== undefined) {
            dispatch(setSelectedQuestionIndex(selectedIndex + 1));
        } else {
            dispatch(setSelectedQuestionIndex(questions.length));
        }
        return true;
    };

    const handleAddQuestion = async () => {
        dispatch(setIsLoading(true));
        const callback = (questionsList: IQuestionNewEditForm[]) => {
            const newQuestionNumber =
                selectedIndex !== null && selectedIndex !== undefined
                    ? selectedIndex + 2
                    : questions.length + 1;
            const baseQuestion = isCampaignPublicWithCode
                ? DEFAULT_FIRST_QUESTION_PUBLIC_WITH_CODE
                : DEFAULT_FIRST_QUESTION;

            const newQuestion = {
                ...baseQuestion,
                id: null,
                questionNumber: newQuestionNumber,
            };
            addQuestionToList(questionsList, newQuestion);
            dispatch(setIsLoading(false));
        };
        checkCurrentSelectQuestionAndSubmit(callback);
    };

    const handleDeleteQuestion = () => {
        const question = questions[selectedIndex || 0];
        const deleteWithAPI = async () => {
            const deleteReponse = await deleteCampaignQuestion(
                currentCampaignId,
                Number(question.id)
            );
            if (deleteReponse.success) {
                getCampaignQuestions();
                toast.success(deleteReponse.message);
            } else {
                toast.error(deleteReponse.message);
            }
        };
        if (question.id) {
            deleteWithAPI();
        } else {
            const newQuestions = questions.filter(
                (_, index) => index !== selectedIndex
            );
            dispatch(setCampaignQuestions(newQuestions));
        }
        dispatch(setSelectedQuestionIndex(null));
    };

    const handleMarkQuestionAsRequired = () => {
        const question = questions[selectedIndex || 0];
        const markWithAPI = async () => {
            const markResponse = await markCampaignQuestionAsRequired(
                currentCampaignId,
                Number(question.id)
            );
            if (markResponse.success) {
                toast.success(markResponse.message);
            } else {
                toast.error(markResponse.message);
            }
        };
        if (question.id) {
            markWithAPI();
        }
        const newQuestions = questions.map((item, index) => {
            if (index === selectedIndex) {
                return {
                    ...item,
                    required: !item.required,
                };
            }
            return item;
        });

        dispatch(setCampaignQuestions(newQuestions));
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const methodsRef = useRef<any[]>([]); // Lưu trữ methods của mỗi form

    const replaceQuestionByQuestionId = (
        questionId: number,
        updatedQuestion: IQuestionNewEditForm
    ) => {
        const questionsList = questions.slice();
        const findQuestion = questionsList.find((q) => q.id === questionId);
        if (findQuestion) {
            const index = questionsList.indexOf(findQuestion);
            questionsList[index] = updatedQuestion;
            dispatch(setCampaignQuestions(questionsList));
        }
    };

    // submit form by id outside form
    const onSubmitCurrentQuestion = async (
        data: IQuestionNewEditForm,
        callbackSyncListQuestion?: (
            questionsList: IQuestionNewEditForm[]
        ) => void
    ) => {
        try {
            const submitData = { ...data };
            let resSuccess = false;
            let resMessage = '';
            let resData: any = null; // eslint-disable-line @typescript-eslint/no-explicit-any
            let resQuestion: IQuestionNewEditForm | null = null;
            if (
                submitData.questionIdForTitle &&
                submitData.questionIdForTitle !== 0 &&
                submitData.mentionValue
            ) {
                submitData.question = submitData.question.replace(
                    submitData.mentionValue,
                    '$title'
                );
            }
            if (submitData.id) {
                const res = await updateCampaignQuestion(
                    currentCampaignId,
                    submitData
                );
                resSuccess = res.success;
                resMessage = res.message;
                resData = res.data;
            } else {
                const res = await createCampaignQuestion(
                    currentCampaignId,
                    submitData
                );
                resSuccess = res.success;
                resMessage = res.message;
                resData = res.data;
            }
            if (resSuccess) {
                resQuestion = listCampaignQuestionsFormatter(resData.question);
                const questionsList = questions.slice();
                const findQuestion = questionsList.find(
                    (q) => q.questionNumber === resQuestion?.questionNumber
                );
                if (findQuestion) {
                    const index = questionsList.indexOf(findQuestion);
                    questionsList[index] = resQuestion;
                }
                if (callbackSyncListQuestion) {
                    callbackSyncListQuestion(questionsList);
                }
                toast.success(resMessage);
            } else {
                dispatch(setIsLoading(false));
                toast.error(resMessage);
            }
            return resSuccess;
        } catch (error) {
            dispatch(setIsLoading(false));
            handleError(error);
        }
    };

    const triggerSubmitQuestion = (
        callbackSyncListQuestion?: (
            questionsList: IQuestionNewEditForm[]
        ) => void
    ) => {
        if (selectedIndex !== null && selectedIndex !== undefined) {
            const selectedMethod = methodsRef.current[selectedIndex];
            selectedMethod.handleSubmit((data: IQuestionNewEditForm) =>
                onSubmitCurrentQuestion(data, callbackSyncListQuestion)
            )();
        }
    };

    const swapQuestion = async (
        from: IQuestionNewEditForm,
        to: IQuestionNewEditForm
    ) => {
        try {
            if (from.id !== to.id) {
                dispatch(setIsLoading(true));
                const swapWithAPI = async () => {
                    const swapResponse = await swapCampaignQuestion(
                        currentCampaignId,
                        {
                            from: Number(from.id),
                            to: Number(to.id),
                        }
                    );
                    if (!swapResponse.success) {
                        toast.error(swapResponse.message);
                    } else {
                        toast.success(swapResponse.message);
                        getCampaignQuestions();
                    }
                };
                await swapWithAPI();
                dispatch(setIsLoading(false));
            }
        } catch (error) {
            dispatch(setIsLoading(false));
            handleError(error);
        }
    };

    const handleCopyQuestion = async (question: IQuestionNewEditForm) => {
        dispatch(setIsLoading(true));
        const callback = async (questionsList: IQuestionNewEditForm[]) => {
            const res = await duplicateCampaignQuestion(
                currentCampaignId,
                Number(question.id)
            );
            dispatch(setIsLoading(false));
            if (res.success) {
                addQuestionToList(
                    questionsList,
                    listCampaignQuestionsFormatter(res.data.question)
                );
                toast.success('Sao chép câu hỏi thành công');
            } else {
                toast.error('Sao chép câu hỏi thất bại');
            }
        };
        checkCurrentSelectQuestionAndSubmit(callback);
    };

    const handleSaveSelectedQuestion = async () => {
        dispatch(setIsLoading(true));
        const callback = (questionsList: IQuestionNewEditForm[]) => {
            dispatch(setSelectedQuestionIndex(null));
            dispatch(setCampaignQuestions(questionsList));
            dispatch(setIsLoading(false));
        };
        checkCurrentSelectQuestionAndSubmit(callback);
    };

    const checkCurrentSelectQuestionAndSubmit = async (
        callbackSyncListQuestion?: (
            questionsList: IQuestionNewEditForm[]
        ) => void
    ) => {
        if (selectedIndex !== null && selectedIndex !== undefined) {
            const selectedMethod = methodsRef.current[selectedIndex];
            const {
                isDirty,
                // errors
            } = selectedMethod.formState;
            const isValid = await selectedMethod.trigger();
            if (isValid) {
                if (isDirty) {
                    triggerSubmitQuestion(callbackSyncListQuestion);
                } else {
                    callbackSyncListQuestion &&
                        callbackSyncListQuestion(questions);
                }
                return true;
            } else {
                // console.log(errors);
                // scroll the error raised element into view
                const errorElement = document.querySelector(
                    '.Mui-error'
                ) as HTMLElement;
                if (errorElement) {
                    errorElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                    });
                }
                dispatch(setIsLoading(false));
                return false;
            }
        } else {
            callbackSyncListQuestion && callbackSyncListQuestion(questions);
            return true;
        }
    };

    const handleDragStart =
        (data: IQuestionNewEditForm) => (event: React.DragEvent) => {
            const from = JSON.stringify(data);
            event.dataTransfer.setData('dragContent', from);
        };

    const handleDragOver =
        (data: IQuestionNewEditForm) => (event: React.DragEvent) => {
            if (data) {
                event.preventDefault();
                return false;
            }
            event.preventDefault();
        };

    const handleDrop =
        (data: IQuestionNewEditForm) => (event: React.DragEvent) => {
            event.preventDefault();
            const from = JSON.parse(event.dataTransfer.getData('dragContent'));
            const to = data;

            setDragSource(from);
            setDragTarget(to);
            // return nếu tới câu hỏi có logic thì không cho swap
            if (to.setting_logic) {
                return;
            }
            setIsModalOpenDrag(true);
        };

    const confirmSwap = () => {
        if (dragSource && dragTarget) {
            swapQuestion(dragSource, dragTarget);
        }
        closeModal();
    };

    const closeModal = () => {
        setIsModalOpenDrag(false);
        setDragSource(null);
        setDragTarget(null);
    };

    const truncateText = (text: string, maxLength = 20) => {
        if (text.length > maxLength) {
            return text.slice(0, maxLength) + '...';
        }
        return text;
    };

    const [isOpenSetupQuota, setIsOpenSetupQuota] = useState(false);
    const handleOpenSetupQuota = () => {
        setIsOpenSetupQuota(true);
    };

    return (
        <CampaignContainer>
            <Grid container spacing={2}>
                <Grid item md={3}>
                    <Box
                        sx={{
                            height: '100%',
                            background: common.white,
                            borderRadius: '15px',
                        }}
                    >
                        <Box
                            sx={{
                                padding: '24px',
                            }}
                        >
                            <FormProvider methods={methods} onSubmit={onSearch}>
                                <BaseTextField
                                    fullWidth
                                    size="small"
                                    name="question"
                                    placeholder="Search"
                                    autoComplete="off"
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            width: '100%',
                                            height: '44px',
                                            borderRadius: '20px',
                                            marginBottom: '30px',
                                        },
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <IconButton type="submit">
                                                <SearchIcon />
                                            </IconButton>
                                        ),
                                    }}
                                />
                            </FormProvider>
                            <Box
                                sx={{
                                    width: '100%',
                                    bgcolor: 'background.paper',
                                }}
                            >
                                <Accordion
                                    defaultExpanded
                                    sx={{
                                        boxShadow: 'none',
                                        '& .MuiAccordionSummary-content': {
                                            padding: '0px',
                                        },
                                        '& .MuiAccordionDetails-root': {
                                            padding: '0px',
                                        },
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        aria-controls="panel1-content"
                                        id="panel1-header"
                                        sx={{
                                            fontWeight: '600',
                                            fontSize: '16px',
                                            lineHeight: '20px',
                                            background: '#F7F7F6',
                                            color: common.black,
                                        }}
                                    >
                                        Tổng số câu hỏi: {questions.length}
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ border: 'none' }}>
                                        <List
                                            component="nav"
                                            aria-label="list-question"
                                            sx={{
                                                maxHeight:
                                                    'calc(100vh - 400px)',
                                                overflowY: 'auto',
                                            }}
                                        >
                                            {questions.map((question, key) => (
                                                <ListItem
                                                    disablePadding
                                                    key={key}
                                                >
                                                    <ListItemButton
                                                        selected={
                                                            selectedIndex ===
                                                            key
                                                        }
                                                        onClick={() =>
                                                            handleItemSelect(
                                                                key
                                                            )
                                                        }
                                                        sx={{
                                                            borderRadius:
                                                                '50px',
                                                            justifyContent:
                                                                'left',
                                                            background:
                                                                selectedIndex ===
                                                                key
                                                                    ? 'var(--color-primary-light-20)'
                                                                    : common.white,
                                                            color:
                                                                selectedIndex ===
                                                                key
                                                                    ? colors.main
                                                                    : common.black,
                                                        }}
                                                    >
                                                        <ListItemIcon
                                                            sx={{
                                                                visibility:
                                                                    selectedIndex ===
                                                                    key
                                                                        ? 'block'
                                                                        : 'hidden',
                                                                color:
                                                                    selectedIndex ===
                                                                    key
                                                                        ? colors.main
                                                                        : common.white,
                                                            }}
                                                        >
                                                            <CheckIcon />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            sx={{
                                                                textAlign:
                                                                    'left',
                                                                fontWeight:
                                                                    '500',
                                                                fontSize:
                                                                    '13px',
                                                                wordWrap:
                                                                    'break-word',
                                                                '& span': {
                                                                    fontSize:
                                                                        '13px',
                                                                    fontWeight:
                                                                        '500',
                                                                },
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display:
                                                                        'flex',
                                                                    alignItems:
                                                                        'center',
                                                                }}
                                                            >
                                                                <Tooltip
                                                                    title={`Q${key + 1}. ${question.question}`}
                                                                    arrow
                                                                >
                                                                    <Typography>
                                                                        {`Q${key + 1}. ${truncateText(question.question) || DEFAULT_QUESTION_CONTENT}`}
                                                                    </Typography>
                                                                </Tooltip>
                                                                <Box
                                                                    sx={{
                                                                        color: 'red',
                                                                        fontSize:
                                                                            '24px',
                                                                        fontWeight: 600,
                                                                        marginLeft:
                                                                            '4px',
                                                                        display:
                                                                            question.required
                                                                                ? 'block'
                                                                                : 'none',
                                                                    }}
                                                                >
                                                                    *
                                                                </Box>
                                                            </Box>
                                                        </ListItemText>
                                                    </ListItemButton>
                                                </ListItem>
                                            ))}
                                        </List>
                                    </AccordionDetails>
                                </Accordion>
                            </Box>
                        </Box>
                    </Box>
                </Grid>
                <Grid item md={9}>
                    <Stack
                        padding={'24px'}
                        spacing={1}
                        sx={{ background: common.white, borderRadius: '15px' }}
                    >
                        <Typography
                            variant="h5"
                            fontWeight={'bold'}
                            sx={{
                                display: 'flex',
                                gap: 1,
                                fontSize: '28px',
                                alignItems: 'center',
                                color: '#000000B2',
                            }}
                        >
                            {currentCampaign?.campaign}
                            {currentCampaign?.editable && (
                                <EditCampaign
                                    style={{
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => setOpenModalCampaign(true)}
                                />
                            )}
                        </Typography>
                        <Stack spacing={2}>
                            <Stack
                                spacing={2}
                                direction={'row'}
                                justifyContent={'space-between'}
                                alignItems={'center'}
                            >
                                <Box sx={{ fontSize: '14px', fontWeight: 400 }}>
                                    <CampaignStatus
                                        status={
                                            currentCampaign?.status_client as string
                                        }
                                    />
                                </Box>

                                <Stack gap={2} direction={'row'}>
                                    <IconButton
                                        size="small"
                                        aria-label="quotas"
                                        sx={{
                                            ...styles.btnView,
                                            display: isCampaignPublicWithCode
                                                ? 'none'
                                                : 'flex',
                                        }}
                                        onClick={() => handleOpenSetupQuota()}
                                    >
                                        <NumbersIcon
                                            sx={{ color: colors.main }}
                                        />
                                    </IconButton>
                                    <Button
                                        variant="contained"
                                        sx={{
                                            ...styles.btnUploadFileExcel,
                                            display: isCampaignPublicWithCode
                                                ? 'none'
                                                : 'flex',
                                        }}
                                        startIcon={<DescriptionOutlinedIcon />}
                                        onClick={() =>
                                            setOpenUploadFileExcel(true)
                                        }
                                    >
                                        Tải bảng câu hỏi bằng Excel
                                    </Button>

                                    <IconButton
                                        size="small"
                                        aria-label="bookmark"
                                        sx={{
                                            ...styles.btnView,
                                            visibility: isCampaignPublicWithCode
                                                ? 'hidden'
                                                : 'visible',
                                        }}
                                        onClick={() =>
                                            navigate(
                                                `/survey/preview/${currentCampaign?.uuid}`
                                            )
                                        }
                                    >
                                        <RemoveRedEyeOutlinedIcon
                                            sx={{ color: colors.main }}
                                        />
                                    </IconButton>
                                </Stack>
                            </Stack>
                            {/* add question */}
                            <Stack
                                sx={{
                                    maxHeight: 'calc(100vh - 400px)',
                                    overflowY: 'auto',
                                }}
                            >
                                {questions.map((item, index) => {
                                    return (
                                        <QuestionNewEditForm
                                            methodsRef={methodsRef}
                                            handleItemSelect={handleItemSelect}
                                            selectedId={selectedIndex}
                                            index={index}
                                            key={index}
                                            question={item}
                                            onDragStart={handleDragStart(item)}
                                            onDragOver={handleDragOver(item)}
                                            onDrop={handleDrop(item)}
                                            handleDeleteQuestion={
                                                handleDeleteQuestion
                                            }
                                            handleMarkQuestionAsRequired={
                                                handleMarkQuestionAsRequired
                                            }
                                            handleCopyQuestion={
                                                handleCopyQuestion
                                            }
                                            replaceQuestionByQuestionId={
                                                replaceQuestionByQuestionId
                                            }
                                            hasTwoOrMoreQuestions={
                                                questions.length > 1
                                            }
                                        />
                                    );
                                })}
                            </Stack>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'end',
                                    gap: 2,
                                }}
                            >
                                <Button
                                    type="button"
                                    variant="contained"
                                    color="primary"
                                    onClick={() => handleSaveSelectedQuestion()}
                                    startIcon={<SaveQuestionIcon />}
                                    sx={{
                                        ...styles.btnFooter,
                                        marginRight: 0,
                                    }}
                                >
                                    Lưu
                                </Button>
                                {questions.length < 80 && (
                                    <Button
                                        type="button"
                                        variant="contained"
                                        color="primary"
                                        startIcon={<AddQuestionIcon />}
                                        onClick={() => {
                                            handleAddQuestion();
                                        }}
                                        sx={styles.btnFooter}
                                    >
                                        Thêm câu hỏi
                                    </Button>
                                )}
                            </Box>
                        </Stack>
                    </Stack>
                </Grid>
            </Grid>
            {openUploadFileExcel && (
                <ModalUplooadFileQuestion
                    open={openUploadFileExcel}
                    onClose={() => setOpenUploadFileExcel(false)}
                    getListQuestion={getCampaignQuestions}
                />
            )}
            {isModalOpenDrag && (
                <ModalConfirmSwapQuestion
                    open={isModalOpenDrag}
                    onClose={closeModal}
                    onOk={confirmSwap}
                    dragSource={dragSource}
                />
            )}
            {openModalCampaign && (
                <CampaignForm
                    actionType={'edit'}
                    open={openModalCampaign}
                    onClose={() => setOpenModalCampaign(false)}
                />
            )}
            {isOpenSetupQuota && (
                <QuotaForm
                    open={isOpenSetupQuota}
                    questions={questions}
                    onClose={() => setIsOpenSetupQuota(false)}
                />
            )}
        </CampaignContainer>
    );
};
const styles = createStyleSheet({
    btnUploadFileExcel: {
        borderRadius: '10px',
        minWidth: '328px',
        textTransform: 'none',
        height: 53,
        fontSize: '18px',
        ':hover': {
            background: colors.main,
        },
    },
    btnView: {
        borderRadius: '10px',
        width: '55px',
        border: `2px solid ${colors.main}`,
    },
    btnFooter: {
        marginRight: '70px',
        borderRadius: '40px',
        minWidth: '230px',
        textTransform: 'none',
        height: 60,
        fontSize: '20px',
    },
});
export default CampaignDesign;
